import { z } from 'zod';
import { Dateish, intList } from './common';

const ISODate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const Competence = z.string().regex(/^\d{4}-\d{2}$/, 'YYYY-MM');
/** valores monetários cruzam o wire como string decimal ("170.05"), padrão Tiflux */
const Money = z.string().regex(/^-?\d+(\.\d{1,2})?$/);

export const ContractModalitySchema = z.enum([
  'free',
  'credit',
  'shared',
  'hours',
  'saas',
  'per_ticket',
  'cumulative_hours',
]);
export type ContractModality = z.infer<typeof ContractModalitySchema>;

export const MODALITY_LABELS: Record<ContractModality, string> = {
  free: 'Livre',
  credit: 'Crédito',
  shared: 'Compartilhado',
  hours: 'Horas',
  saas: 'SaaS/Produto',
  per_ticket: 'Por Atendimento',
  cumulative_hours: 'Horas Cumulativas',
};

// ── Tipos de contrato ───────────────────────────────────────────────────────
export const ContractTypeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  modality: ContractModalitySchema,
  active: z.boolean(),
});
export type ContractType = z.infer<typeof ContractTypeSchema>;

export const ContractTypeBodySchema = z.object({
  name: z.string().min(1),
  modality: ContractModalitySchema,
});

// ── Contratos ───────────────────────────────────────────────────────────────
export const ContractStatusSchema = z.enum(['active', 'expired', 'canceled']);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;

export const ContractSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  clientName: z.string(),
  contractTypeId: z.number().int(),
  contractTypeName: z.string(),
  modality: ContractModalitySchema,
  name: z.string(),
  value: Money,
  dueDay: z.number().int(),
  startDate: ISODate,
  expirationDate: ISODate.nullable(),
  automaticRenewal: z.boolean(),
  includedMinutes: z.number().int().nullable(),
  surplusHourValue: Money.nullable(),
  technicalObservations: z.string().nullable(),
  /** derivado: canceledAt > canceled; expirationDate < hoje > expired; senão active */
  status: ContractStatusSchema,
  createdAt: Dateish,
});
export type Contract = z.infer<typeof ContractSchema>;

export const ContractBodySchema = z.object({
  clientId: z.number().int().positive(),
  contractTypeId: z.number().int().positive(),
  name: z.string().min(1),
  value: Money.default('0'),
  dueDay: z.number().int().min(1).max(28).default(10),
  startDate: ISODate,
  expirationDate: ISODate.nullish(),
  automaticRenewal: z.boolean().default(false),
  includedMinutes: z.number().int().min(0).nullish(),
  surplusHourValue: Money.nullish(),
  technicalObservations: z.string().nullish(),
});
export type ContractBody = z.infer<typeof ContractBodySchema>;

export const ContractListQuerySchema = z.object({
  client_ids: intList.optional(),
  status: z.enum(['active', 'expired', 'canceled', 'all']).default('active'),
});
export type ContractListQuery = z.infer<typeof ContractListQuerySchema>;

// ── Financeiro v9: deslocamentos / serviços avulsos / valores extras ────────
export const DisplacementSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  value: Money,
  /** null = Todos os clientes */
  clientId: z.number().int().nullable(),
  clientName: z.string().nullable(),
  /** só quando clientId definido */
  contractId: z.number().int().nullable(),
  contractName: z.string().nullable(),
  createdAt: Dateish,
});
export type Displacement = z.infer<typeof DisplacementSchema>;

export const DisplacementBodySchema = z.object({
  name: z.string().min(1),
  value: Money,
  clientId: z.number().int().positive().nullish(),
  contractId: z.number().int().positive().nullish(),
});
export type DisplacementBody = z.infer<typeof DisplacementBodySchema>;

export const LooseServiceSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** null = Todos os clientes */
  clientId: z.number().int().nullable(),
  clientName: z.string().nullable(),
  internalHourValue: Money,
  remoteHourValue: Money,
  externalHourValue: Money,
  createdAt: Dateish,
});
export type LooseService = z.infer<typeof LooseServiceSchema>;

export const LooseServiceBodySchema = z.object({
  name: z.string().min(1),
  clientId: z.number().int().positive().nullish(),
  internalHourValue: Money,
  remoteHourValue: Money,
  externalHourValue: Money,
});
export type LooseServiceBody = z.infer<typeof LooseServiceBodySchema>;

export const ExtraValueSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  unitValue: Money,
  description: z.string().nullable(),
  createdAt: Dateish,
});
export type ExtraValue = z.infer<typeof ExtraValueSchema>;

export const ExtraValueBodySchema = z.object({
  name: z.string().min(1),
  unitValue: Money,
  description: z.string().nullish(),
});
export type ExtraValueBody = z.infer<typeof ExtraValueBodySchema>;

// ── Lançamentos no ticket (ticket_charges) ──────────────────────────────────

/** decimal de até 2 casas ("1.5", "120.00", "-0.05") → centavos INTEIROS */
const DECIMAL2_RE = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;
function decimalToCents(value: string): bigint {
  const m = DECIMAL2_RE.exec(value);
  if (!m) throw new Error(`invalid decimal string: ${JSON.stringify(value)}`);
  const [, sign, int = '0', frac = ''] = m;
  const cents = BigInt(int) * 100n + BigInt(frac.padEnd(2, '0'));
  return sign === '-' ? -cents : cents;
}

/**
 * REGRA ÚNICA do total de um lançamento: quantity × unitValue em centavos
 * INTEIROS. Aritmética 100% inteira (BigInt) sobre os dígitos das strings
 * decimais — float nunca entra. Arredonda meio-centavo para LONGE do zero,
 * exatamente como o `round()` numeric do Postgres — o agregado SQL
 * `round(quantity * unit_value * 100)` e este helper são pinados um no outro
 * por teste de paridade (billing-finance.repository.db.test.ts).
 */
export function chargeTotalCents(quantity: string, unitValue: string): number {
  // qCents × uCents = total em centavos × 100; divide por 100 arredondando
  const prod = decimalToCents(quantity) * decimalToCents(unitValue);
  const q = prod / 100n;
  const r = prod % 100n; // mesmo sinal de prod
  const rounded = r >= 50n ? q + 1n : r <= -50n ? q - 1n : q;
  return Number(rounded);
}

export const ChargeKindSchema = z.enum(['displacement', 'service', 'extra']);
export type ChargeKind = z.infer<typeof ChargeKindSchema>;

export const CHARGE_KIND_LABELS: Record<ChargeKind, string> = {
  displacement: 'Deslocamento',
  service: 'Serviço avulso',
  extra: 'Valor extra',
};

export const ServiceKindSchema = z.enum(['internal', 'remote', 'external']);
export type ServiceKind = z.infer<typeof ServiceKindSchema>;

export const SERVICE_KIND_LABELS: Record<ServiceKind, string> = {
  internal: 'Interno',
  remote: 'Remoto',
  external: 'Externo',
};

/** quantidade decimal com até 2 casas (para service = horas decimais) */
const Quantity = z.string().regex(/^\d+(\.\d{1,2})?$/);

export const TicketChargeSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  kind: ChargeKindSchema,
  /** id do cadastro na criação (sem FK — o lançamento é snapshot) */
  sourceId: z.number().int().nullable(),
  name: z.string(),
  /** só kind=service */
  serviceKind: ServiceKindSchema.nullable(),
  quantity: Quantity,
  unitValue: Money,
  /** derivado no servidor: quantity × unitValue em centavos inteiros */
  total: Money,
  createdById: z.number().int().nullable(),
  createdByName: z.string().nullable(),
  createdAt: Dateish,
});
export type TicketCharge = z.infer<typeof TicketChargeSchema>;

export const TicketChargeCreateBodySchema = z.object({
  kind: ChargeKindSchema,
  /** cadastro de origem: o servidor snapshota name/unitValue dele */
  sourceId: z.number().int().positive().optional(),
  /** obrigatório SEM sourceId; com sourceId o nome vem do cadastro */
  name: z.string().min(1).optional(),
  /** obrigatório sse kind=service (escolhe o valor-hora default do cadastro) */
  serviceKind: ServiceKindSchema.optional(),
  quantity: Quantity.default('1'),
  /** sobrescreve o valor do cadastro; obrigatório sem sourceId */
  unitValue: Money.optional(),
});
export type TicketChargeCreateBody = z.infer<typeof TicketChargeCreateBodySchema>;

// ── Faturamentos pendentes ──────────────────────────────────────────────────
export const PendingBillingSchema = z.object({
  contractId: z.number().int(),
  contractName: z.string(),
  clientId: z.number().int(),
  clientName: z.string(),
  modality: ContractModalitySchema,
  competence: Competence,
  /** vencimento sugerido (dueDay do contrato na competência seguinte) */
  suggestedDueDate: ISODate,
  baseValue: Money,
  /** detalhe do consumo (modalidade hours/per_ticket + lançamentos v9) */
  consumption: z.object({
    workedMinutes: z.number().int().nullable(),
    includedMinutes: z.number().int().nullable(),
    surplusMinutes: z.number().int().nullable(),
    surplusValue: Money.nullable(),
    closedTickets: z.number().int().nullable(),
    /** lançamentos da competência (v9) — null quando o agregado não foi lido */
    displacementsValue: Money.nullable(),
    displacementsCount: z.number().int().nullable(),
    servicesValue: Money.nullable(),
    /** soma das quantidades (horas decimais) dos lançamentos de serviço */
    servicesHours: z.number().nullable(),
    extrasValue: Money.nullable(),
    extrasCount: z.number().int().nullable(),
    summary: z.string(),
  }),
});
export type PendingBilling = z.infer<typeof PendingBillingSchema>;

export const PendingBillingQuerySchema = z.object({
  competence: Competence,
  client_ids: intList.optional(),
});

// ── Faturas (histórico) ─────────────────────────────────────────────────────
export const InvoiceStatusSchema = z.enum(['billed', 'paid', 'reversed']);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const InvoiceSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  clientName: z.string(),
  contractId: z.number().int(),
  contractName: z.string(),
  competence: Competence,
  billingDate: ISODate,
  dueDate: ISODate,
  baseValue: Money,
  discountValue: Money,
  interestValue: Money,
  realValue: Money,
  nfeNumber: z.string().nullable(),
  observations: z.string().nullable(),
  consumptionSummary: z.string().nullable(),
  status: InvoiceStatusSchema,
  paidAt: Dateish.nullable(),
  // ── cobrança via provedor (v9 Etapa 2; null = cobrança nunca gerada) ───────
  /** provedor que emitiu a cobrança ('asaas' hoje; campos genéricos de
   *  propósito — um provedor novo não muda o contrato da fatura) */
  paymentProvider: z.string().nullable(),
  externalPaymentId: z.string().nullable(),
  paymentUrl: z.string().nullable(),
  /** último status reportado pelo provedor (PENDING/RECEIVED/CONFIRMED/OVERDUE…) */
  paymentStatus: z.string().nullable(),
  createdAt: Dateish,
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoiceCreateBodySchema = z.object({
  contractId: z.number().int().positive(),
  competence: Competence,
  billingDate: ISODate,
  dueDate: ISODate,
  discountValue: Money.default('0'),
  interestValue: Money.default('0'),
  nfeNumber: z.string().nullish(),
  observations: z.string().nullish(),
});
export type InvoiceCreateBody = z.infer<typeof InvoiceCreateBodySchema>;

// ── Integração Asaas (v9 Etapa 2) ───────────────────────────────────────────
export const AsaasEnvironmentSchema = z.enum(['sandbox', 'production']);
export type AsaasEnvironment = z.infer<typeof AsaasEnvironmentSchema>;

/**
 * Visão da integração para o admin (GET /billing/integrations/asaas). A api
 * key NUNCA volta inteira (só ****últimos4); o webhookToken volta COMPLETO de
 * propósito — o operador precisa colá-lo no campo "authToken" do webhook no
 * painel do Asaas, junto com a webhookUrl. A rota é integrations.manage +
 * admin-only, mesma superfície que enxerga as credenciais do WhatsApp.
 */
export const FinanceIntegrationSchema = z.object({
  apiKeyMasked: z.string().nullable(),
  environment: AsaasEnvironmentSchema.nullable(),
  active: z.boolean(),
  /** token que autentica o webhook (header asaas-access-token) */
  webhookToken: z.string().nullable(),
  /** URL pública a registrar no Asaas: `${API_PUBLIC_URL}/api/v1/inbound/asaas` */
  webhookUrl: z.string().nullable(),
});
export type FinanceIntegration = z.infer<typeof FinanceIntegrationSchema>;

/** PUT parcial: apiKey obrigatória só na PRIMEIRA configuração (o use-case
 *  responde 422 api_key_required quando não há linha ainda). */
export const FinanceIntegrationPutBodySchema = z.object({
  apiKey: z.string().min(1).optional(),
  environment: AsaasEnvironmentSchema.optional(),
  active: z.boolean().optional(),
});
export type FinanceIntegrationPutBody = z.infer<typeof FinanceIntegrationPutBodySchema>;

export const InvoiceListQuerySchema = z.object({
  billing_start: ISODate.optional(),
  billing_end: ISODate.optional(),
  client_ids: intList.optional(),
  status: z.enum(['billed', 'paid', 'reversed', 'all']).default('all'),
  nfe: z.string().optional(),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;
